// ─────────────────────────────────────────────────────────────────────────
// Pagos a proveedores (26-ago-2026, Fase F área contable).
//
// Las facturas de COMPRA entran solas por el sync del SII; acá se les hace
// seguimiento de pago: vencimiento = fecha + días del proveedor (30 default,
// editable en la ficha del Contacto), semáforo vencida/por vencer/por pagar,
// y marcar pagada (fecha/medio) — base de la conciliación bancaria (Fase E).
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import MedioPagoModal from "../../components/MedioPagoModal";
import { formatPrice } from "../../utils";

const fmt = n => `${n < 0 ? "−" : ""}$${formatPrice(Math.round(Math.abs(n || 0)))}`;
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short" }) : "—";
const num = { fontVariantNumeric: "tabular-nums" };

const ESTADO = {
  vencida:    { label: "VENCIDA",     color: "#e74c3c", bg: "#2d1515" },
  por_vencer: { label: "Vence pronto",color: "#f39c12", bg: "#2d2000" },
  por_pagar:  { label: "Por pagar",   color: "#3498db", bg: "#0d2035" },
  pagada:     { label: "Pagada",      color: "#2ecc71", bg: "#0d2a15" },
  nc:         { label: "NC",          color: "#8a8a8a", bg: "#222" },
};

const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  // Tabla desktop (pulido 27-ago)
  th: {
    background: "#1f1f1f", padding: "8px 12px", textAlign: "left", fontSize: 10,
    fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e",
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 12px", color: "#bbb", borderBottom: "1px solid #252525",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
};

const Pagos = () => {
  // Pulido desktop 27-ago: móvil intacto (tarjetas); desktop = tabla densa.
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("pendientes"); // pendientes | todas | pagadas
  const [marcando, setMarcando] = useState(null);
  const [pagoModal, setPagoModal] = useState(null);   // factura a marcar pagada (mini-modal)

  const cargar = async () => {
    try {
      const r = await backend.get("sii/pagos", { params: { meses: 4 } });
      setData(r.data); setError(null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };
  useEffect(() => { cargar(); }, []);

  // Marcar pagada → mini-modal con presets (28-ago, adiós window.prompt);
  // despagar va directo sin preguntar medio.
  const marcarPago = (f, pagada) => {
    if (pagada) { setPagoModal(f); return; }
    aplicarPago(f, false, "");
  };

  const aplicarPago = async (f, pagada, medio) => {
    setMarcando(f._id);
    try {
      await backend.patch(`sii/facturas/${f._id}/pago`, { pagada, medio });
      setPagoModal(null);
      await cargar();
    } catch (e) { alert(e?.response?.data?.error || e.message); }
    setMarcando(null);
  };

  if (error) return <div style={{ color: "#e74c3c", fontSize: 12, padding: 12 }}>Error: {error}</div>;
  if (!data) return <div style={{ color: "#8a8a8a", fontSize: 12, padding: 12 }}>Calculando vencimientos…</div>;

  const { resumen, filas } = data;
  const visibles = filas.filter(f =>
    filtro === "todas" ? true
    : filtro === "pagadas" ? f.estado === "pagada"
    : filtro === "pendientes" ? ["vencida", "por_vencer", "por_pagar"].includes(f.estado)
    : f.estado === filtro); // filtro por estado exacto (tarjetas KPI cliqueables)

  // Tarjetas KPI CLIQUEABLES (feedback Maxi 27-ago): tocar filtra la lista.
  const kpis = [
    { key: "vencidas",   estado: "vencida",    label: "Vencidas",         color: "#e74c3c" },
    { key: "por_vencer", estado: "por_vencer", label: "Vencen en 7 días", color: "#f39c12" },
    { key: "por_pagar",  estado: "por_pagar",  label: "Por pagar",        color: "#3498db" },
    { key: "pagadas",    estado: "pagada",     label: "Pagadas (rango)",  color: "#2ecc71" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {kpis.map(k => (
          <div key={k.key}
            onClick={() => setFiltro(filtro === k.estado ? "pendientes" : k.estado)}
            className="t-02 tap-card"
            style={{ ...S.card, flex: isMobile ? "1 1 42%" : "1 1 0", minWidth: 150, cursor: "pointer",
              border: `1px solid ${filtro === k.estado ? k.color : "#2e2e2e"}` }}>
            <div style={S.label}>{k.label}{filtro === k.estado ? " ✕" : ""}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: k.color, ...num }}>
              {fmt(resumen[k.key].monto)}
              <span style={{ fontSize: 11, fontWeight: 600, color: "#8a8a8a", marginLeft: 6 }}>({resumen[k.key].n})</span>
            </div>
          </div>
        ))}
      </div>

      <div className="chips-scroll" style={{ display: "flex", gap: 6 }}>
        {[["pendientes", "Pendientes"], ["pagadas", "Pagadas"], ["todas", "Todas"]].map(([k, l]) => (
          <button key={k} type="button" className="chip-touch no-touch-min t-02"
            onClick={() => setFiltro(k)}
            style={{
              background: filtro === k ? "#3a1114" : "#1e1e1e",
              color: filtro === k ? "#ff8a8f" : "#999",
              border: `1px solid ${filtro === k ? "#e30613" : "#333"}`,
              borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer",
            }}>{l}</button>
        ))}
        <span style={{ fontSize: 10, color: "#8a8a8a", alignSelf: "center" }}>
          Los días de pago se editan en la ficha del proveedor (Contactos)
        </span>
      </div>

      {visibles.length === 0 && (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>Nada acá ✓</div>
      )}
      {visibles.length > 0 && !isMobile && (
        /* ── Desktop: tabla densa ── */
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
            <colgroup>
              <col />                            {/* Proveedor */}
              <col style={{ width: "80px" }} />  {/* Folio */}
              <col style={{ width: "90px" }} />  {/* Emitida */}
              <col style={{ width: "110px" }} /> {/* Vence */}
              <col style={{ width: "130px" }} /> {/* Total */}
              <col style={{ width: "110px" }} /> {/* Estado */}
              <col style={{ width: "150px" }} /> {/* Acción */}
            </colgroup>
            <thead>
              <tr>
                <th style={S.th}>Proveedor</th>
                <th style={S.th}>Folio</th>
                <th style={S.th}>Emitida</th>
                <th style={S.th}>Vence</th>
                <th style={{ ...S.th, textAlign: "right" }}>Total</th>
                <th style={S.th}>Estado</th>
                <th style={S.th}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f, i) => {
                const e = ESTADO[f.estado] || ESTADO.por_pagar;
                return (
                  <tr key={f._id}
                    style={{ background: i % 2 === 0 ? "#232323" : "#212121" }}
                    onMouseEnter={ev => Array.from(ev.currentTarget.cells).forEach(c => c.style.background = "#2a2a2a")}
                    onMouseLeave={ev => Array.from(ev.currentTarget.cells).forEach(c => c.style.background = "")}>
                    <td style={{ ...S.td, color: "#fff", fontWeight: 700 }}>
                      {f.proveedor}
                      {f.pago?.pagada && (
                        <span style={{ color: "#2ecc71", fontWeight: 400, fontSize: 11, ...num }}>
                          {" "}· pagada {fmtD(f.pago.fecha)}{f.pago.medio ? " " + f.pago.medio : ""}
                        </span>
                      )}
                    </td>
                    <td style={{ ...S.td, fontFamily: "monospace", color: "#3498db", fontWeight: 700 }}>F{f.folio}</td>
                    <td style={{ ...S.td, ...num }}>{fmtD(f.fecha)}</td>
                    <td style={{ ...S.td, color: e.color, ...num }}>{fmtD(f.vencimiento)} ({f.dias}d)</td>
                    <td style={{ ...S.td, textAlign: "right", color: "#fff", fontWeight: 800, ...num }}>{fmt(f.montoTotal)}</td>
                    <td style={{ ...S.td, overflow: "visible" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color: e.color, background: e.bg, whiteSpace: "nowrap" }}>{e.label}</span>
                    </td>
                    <td style={{ ...S.td, overflow: "visible" }}>
                      {f.estado !== "nc" && (
                        <button
                          onClick={() => marcarPago(f, !f.pago?.pagada)}
                          disabled={marcando === f._id}
                          className="t-02"
                          style={{
                            background: f.pago?.pagada ? "#2a2a2a" : "#0d2a15",
                            color: f.pago?.pagada ? "#999" : "#2ecc71",
                            border: `1px solid ${f.pago?.pagada ? "#3a3a3a" : "#0d4020"}`,
                            borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                          }}>
                          {marcando === f._id ? "…" : f.pago?.pagada ? "Despagar" : "✓ Marcar pagada"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {isMobile && visibles.map(f => {
        const e = ESTADO[f.estado] || ESTADO.por_pagar;
        return (
          <div key={f._id} style={{ ...S.card, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.proveedor}
              </div>
              <div style={{ fontSize: 11, color: "#8a8a8a", ...num }}>
                F{f.folio} · emitida {fmtD(f.fecha)} · <span style={{ color: e.color }}>vence {fmtD(f.vencimiento)}</span> ({f.dias}d)
                {f.pago?.pagada && ` · pagada ${fmtD(f.pago.fecha)}${f.pago.medio ? " " + f.pago.medio : ""}`}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#fff", ...num }}>{fmt(f.montoTotal)}</div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color: e.color, background: e.bg }}>{e.label}</span>
            </div>
            {f.estado !== "nc" && (
              <button
                onClick={() => marcarPago(f, !f.pago?.pagada)}
                disabled={marcando === f._id}
                className="no-touch-min t-02"
                style={{
                  background: f.pago?.pagada ? "#2a2a2a" : "#0d2a15",
                  color: f.pago?.pagada ? "#999" : "#2ecc71",
                  border: `1px solid ${f.pago?.pagada ? "#3a3a3a" : "#0d4020"}`,
                  borderRadius: 8, padding: "8px 14px", fontSize: 11, fontWeight: 700,
                  cursor: "pointer", flexShrink: 0, minHeight: 38,
                }}>
                {marcando === f._id ? "…" : f.pago?.pagada ? "Despagar" : "✓ Marcar pagada"}
              </button>
            )}
          </div>
        );
      })}

      {/* ── Mini-modal medio de pago (28-ago) ── */}
      <MedioPagoModal
        open={!!pagoModal}
        titulo={pagoModal ? `Marcar pagada · F${pagoModal.folio}` : ""}
        subtitulo={pagoModal ? `${pagoModal.proveedor || "—"} · ${fmt(pagoModal.montoTotal)}` : ""}
        onCancel={() => setPagoModal(null)}
        onConfirm={medio => aplicarPago(pagoModal, true, medio)}
      />
    </div>
  );
};

export default Pagos;
